import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronLeft, TrendingUp, TrendingDown, Activity, Heart, Smile, Sparkles, Minus } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import api from "../lib/api.js";
import { totalInsight, activeVsPassiveInsight, postureScoreInsight, moodStressInsight } from "../lib/insights.js";

export default function Progress() {
  const nav = useNavigate();
  const [days, setDays] = useState([]);
  const [tab, setTab] = useState("week");

  useEffect(() => {
    api.get("/progress/weekly").then((r) => setDays(r.data.days));
  }, []);

  const sliceLen = tab === "week" ? 7 : tab === "month" ? 28 : 14;
  const data = days.slice(-sliceLen).map((d) => ({
    date: d.date.slice(5),
    Total: d.total, Active: d.active, Passive: d.passive,
    Score: d.posture_score, Mood: d.mood ?? 0, Stress: d.stress ?? 0,
  }));

  const totalSteps = data.reduce((a, x) => a + x.Total, 0);
  const avgScore = data.length ? Math.round(data.reduce((a, x) => a + x.Score, 0) / data.length) : 0;

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28 relative">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="progress-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-5 ps-fade-up">
            <div className="ps-chip mb-3">
              <TrendingUp size={11} /> Your trends
            </div>
            <h1 className="ps-heading text-4xl font-extrabold leading-[1]">
              Progress<span className="ps-serif ps-italic font-normal text-slate-500">.</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2 ps-serif ps-italic">Walking, posture, and wellness — over time.</p>
          </div>

          {/* Hero summary */}
          <div className="mt-5 grid grid-cols-2 gap-3 ps-stagger">
            <div className="ps-card p-4 relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-[#4ade80]/15 blur-2xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#4ade80] font-bold">Period total</div>
                <div className="ps-heading text-2xl font-extrabold mt-1 ps-tabular">{totalSteps.toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 ps-serif ps-italic">steps</div>
              </div>
            </div>
            <div className="ps-card p-4 relative overflow-hidden">
              <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-[#a78bfa]/15 blur-2xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a78bfa] font-bold">Avg score</div>
                <div className="ps-heading text-2xl font-extrabold mt-1 ps-tabular">{avgScore}<span className="text-base text-slate-500">/100</span></div>
                <div className="text-[10px] text-slate-500 ps-serif ps-italic">posture score</div>
              </div>
            </div>
          </div>

          {/* Period selector */}
          <div className="mt-5 flex gap-1 bg-[#0E1F2A]/60 backdrop-blur border border-[#1B364A] rounded-full p-1">
            {[{ k: "week", l: "Week" }, { k: "two", l: "2 Weeks" }, { k: "month", l: "Month" }].map((t) => (
              <button
                key={t.k}
                data-testid={`progress-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`flex-1 py-2 rounded-full text-xs font-bold transition-all ${tab === t.k ? "bg-gradient-to-br from-[#4ade80] to-[#34d399] text-[#022014] shadow-[0_4px_12px_-4px_rgba(74,222,128,0.5)]" : "text-slate-400 hover:text-white"}`}
              >{t.l}</button>
            ))}
          </div>

          <ChartCard
            label="Total Posture Steps"
            icon={Activity}
            color="#4ade80"
            italic="movement + alignment"
            insight={totalInsight(data)}
            testId="insight-total"
          >
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#5C748A" fontSize={10} />
                <YAxis stroke="#5C748A" fontSize={10} />
                <Tooltip contentStyle={tooltipStyle} />
                <CartesianGrid stroke="#1B364A" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="Total" stroke="#4ade80" strokeWidth={2.5} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            label="Active vs Passive"
            icon={Activity}
            color="#22d3ee"
            italic="movement split"
            insight={activeVsPassiveInsight(data)}
            testId="insight-active-passive"
          >
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <XAxis dataKey="date" stroke="#5C748A" fontSize={10} />
                <YAxis stroke="#5C748A" fontSize={10} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="Active" stroke="#4ade80" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Passive" stroke="#22d3ee" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            label="Posture Score Trend"
            icon={Heart}
            color="#a78bfa"
            italic="0–100 over time"
            insight={postureScoreInsight(data)}
            testId="insight-score"
          >
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#5C748A" fontSize={10} />
                <YAxis stroke="#5C748A" fontSize={10} domain={[0, 100]} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="Score" stroke="#a78bfa" strokeWidth={2.5} fill="url(#g2)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            label="Mood vs Stress"
            icon={Smile}
            color="#FBBF24"
            italic="from your check-ins"
            insight={moodStressInsight(data)}
            testId="insight-mood-stress"
          >
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <XAxis dataKey="date" stroke="#5C748A" fontSize={10} />
                <YAxis stroke="#5C748A" fontSize={10} domain={[0, 10]} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="Mood" stroke="#84cc16" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Stress" stroke="#FBBF24" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
      <BottomNav />
    </PhoneFrame>
  );
}

const tooltipStyle = {
  background: "rgba(14, 31, 42, 0.92)",
  border: "1px solid #1B364A",
  borderRadius: 12,
  fontSize: 12,
  backdropFilter: "blur(8px)",
};

function ChartCard({ label, icon: Icon, color, italic, children, insight, testId }) {
  return (
    <div className="mt-5 ps-card p-5 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full blur-3xl opacity-20" style={{ background: color }} />
      <div className="relative flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}1f`, border: `1px solid ${color}33` }}>
          <Icon size={15} color={color} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color }}>{label}</div>
          <div className="text-[10px] text-slate-500 ps-serif ps-italic">{italic}</div>
        </div>
      </div>
      <div className="relative">{children}</div>
      {insight && <InsightStrip insight={insight} testId={testId} />}
    </div>
  );
}

function InsightStrip({ insight, testId }) {
  const ToneIcon = insight.tone === "up" ? TrendingUp
    : insight.tone === "down" ? TrendingDown
    : insight.tone === "flat" ? Minus
    : Sparkles;
  return (
    <div
      className="relative mt-4 p-4 rounded-2xl border flex gap-3 items-start"
      data-testid={testId}
      style={{
        background: `linear-gradient(135deg, ${insight.accent}14 0%, transparent 70%)`,
        borderColor: `${insight.accent}40`,
      }}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{
          background: `${insight.accent}22`,
          border: `1px solid ${insight.accent}55`,
          boxShadow: `0 0 16px ${insight.accent}33`,
        }}
      >
        <ToneIcon size={16} color={insight.accent} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-[10px] uppercase tracking-[0.25em] font-bold"
          style={{ color: insight.accent }}
        >
          {insight.label}
        </div>
        <div className="ps-heading text-[15px] font-extrabold mt-0.5 leading-snug" data-testid={`${testId}-headline`}>
          {insight.headline}
        </div>
        <div className="text-xs text-slate-400 leading-snug mt-1 ps-serif">
          {insight.body}
        </div>
      </div>
    </div>
  );
}
